import {
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Popover,
  Title,
} from '@patternfly/react-core'
import {
  CheckIcon,
  TimesIcon,
  ExclamationTriangleIcon,
} from '@patternfly/react-icons'
import * as React from 'react'
import { useMemo, useState } from 'react'

import { PhoneNumberTypeId } from '@app/domain/patient/patient.interface'
import { ActionLink } from '@app/utils/styles/action-link'
import { theme } from '@app/utils/theme'

interface CommunicationStatusDetailsProps {
  emailAddress?: string
  consentsToEmail: boolean
  phoneNumber?: string
  phoneNumberType?: string
  consentsToSms: boolean
}

const ConsentBadge: React.FC<{ consent: boolean }> = ({ consent }) => (
  <div style={{ color: consent ? theme.colors.success : theme.colors.error }}>
    {consent ? <CheckIcon /> : <TimesIcon />} Communication Consent
  </div>
)

export const CommunicationStatusDetails = ({
  emailAddress,
  consentsToEmail,
  phoneNumber,
  phoneNumberType,
  consentsToSms,
}: CommunicationStatusDetailsProps) => {
  const [isPopoverVisible, setPopoverVisible] = useState(false)

  const canSendEmail = !!emailAddress && consentsToEmail
  const canSendSMS =
    phoneNumber != null &&
    phoneNumberType === PhoneNumberTypeId.Mobile &&
    consentsToSms
  const canContact = canSendSMS || canSendEmail

  const bodyContent = useMemo(() => {
    const hasEmail = !!emailAddress
    const hasMobile =
      phoneNumber != null && phoneNumberType === PhoneNumberTypeId.Mobile

    return (
      <Flex direction={{ default: 'column' }}>
        {hasEmail ? (
          <FlexItem>
            <Grid md={6}>
              <GridItem span={12}>Email</GridItem>
              <GridItem span={6}>{emailAddress}</GridItem>
              <GridItem span={6}>
                <ConsentBadge consent={consentsToEmail} />
              </GridItem>
            </Grid>
          </FlexItem>
        ) : null}

        {hasMobile ? (
          <FlexItem>
            <Grid md={6}>
              <GridItem span={12}>SMS</GridItem>
              <GridItem span={6}>{phoneNumber}</GridItem>
              <GridItem span={6}>
                <ConsentBadge consent={consentsToSms} />
              </GridItem>
            </Grid>
          </FlexItem>
        ) : null}

        {hasMobile && !consentsToSms ? (
          <FlexItem>
            <div style={{ maxWidth: '28rem' }}>
              Once consent of SMS communication has been revoked, patients can
              opt back in by texting START to your SMS number +15551234567
            </div>
          </FlexItem>
        ) : null}
      </Flex>
    )
  }, [
    consentsToEmail,
    consentsToSms,
    emailAddress,
    phoneNumber,
    phoneNumberType,
  ])

  return (
    <Popover
      headerContent={
        <Title headingLevel="h2" size="lg">
          Communication Details
        </Title>
      }
      isVisible={isPopoverVisible}
      shouldClose={() => setPopoverVisible(false)}
      bodyContent={bodyContent}
      hasAutoWidth
    >
      <ActionLink
        onClick={() => setPopoverVisible(true)}
        style={canContact ? undefined : { color: theme.colors.warning }}
        aria-expanded={isPopoverVisible}
        aria-haspopup="dialog"
      >
        {canContact ? (
          <>Deliverable</>
        ) : (
          <>
            <ExclamationTriangleIcon /> Undeliverable
          </>
        )}
      </ActionLink>
    </Popover>
  )
}
